import { useEffect, useState } from "react";

import { ColumnDef, Table, TableSearch } from "@/components/ui/Table";
import { MODAL_ICONS } from "@/components/ui/AppModal";
import CouponModal from "@features/coupons/CouponModal";
import {
  Coupon,
  CouponFormValues,
  couponToFormValues,
  eventContentsToOptions,
  formatCouponValue,
  getContentNames,
  normalizeLabel,
  toDateOnly,
} from "@/features/coupons/couponUtils";
import { useEventsFeature } from "@/features/events/useEventsFeature";
import {
  useCreateCoupon,
  useDeleteBillingCoupon,
  useGetCouponsByCompany,
  useUpdateCoupon,
} from "@/graphql/hooks/useAdmin";
import { useAppSelector } from "@/store/hooks";
import { humanizeLabel } from "@/lib/utils/humanize";

interface CouponsRow {
  id: string;
  internalCodeName: string;
  promoCode: string;
  content: string;
  value: string;
  discountType: string;
  startDate: string;
  endDate: string;
  singleUseCompany: string;
  singleUseAttendee: string;
  employeeCode: string;
  usage: number;
  status: string;
  raw: Coupon;
}

export default function CouponsPage() {
  const [search, setSearch] = useState("");
  const activeCompany = useAppSelector((s) => s.company.activeCompany);
  const { currentEvent, eventContents } = useEventsFeature();
  const { coupons, loading, error, refetch } = useGetCouponsByCompany(
    activeCompany?.id ?? "",
    currentEvent?.id,
  );

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<"create" | "edit">("create");
  const [editingCoupon, setEditingCoupon] = useState<Coupon | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const contentOptions = eventContentsToOptions(eventContents);

  const rows: CouponsRow[] = (coupons ?? []).map((c: Coupon) => ({
    id: c.id,
    internalCodeName: c.name ?? "-",
    promoCode: c.promoCode ?? "-",
    content: getContentNames(c.contentIds, eventContents),
    value: formatCouponValue(c.value, c.unit),
    discountType: normalizeLabel(c.unit),
    startDate: toDateOnly(c.startDate),
    endDate: toDateOnly(c.endDate),
    singleUseCompany: c.oneUsePerCompany ? "Yes" : "No",
    singleUseAttendee: c.oneUsePerUser ? "Yes" : "No",
    employeeCode: c.oneUsePerUser && c.oneUsePerCompany ? "No" : "Yes",
    usage:
      (c.usedByUserIds?.length ?? 0) +
      (c.usedByCompanyMembershipIds?.length ?? 0),
    status: normalizeLabel(c.status),
    raw: c,
  }));

  const filtered = rows.filter(
    (r) =>
      r.internalCodeName.toLowerCase().includes(search.toLowerCase()) ||
      r.promoCode.toLowerCase().includes(search.toLowerCase()) ||
      r.value.toLowerCase().includes(search.toLowerCase()),
  );

  // ── Modal open helpers ─────────────────────────────────────────
  const openCreateModal = () => {
    setModalMode("create");
    setEditingCoupon(null);
    setModalOpen(true);
  };

  const openEditModal = (row: CouponsRow) => {
    setModalMode("edit");
    setEditingCoupon(row.raw);
    setModalOpen(true);
  };

  const { createCoupon, loading: couponLoading } = useCreateCoupon();
  const { updateCoupon } = useUpdateCoupon();
  const { deleteBillingCoupon } = useDeleteBillingCoupon();

  // ── action functions ───────────────────────────────
  const parseDateToTimestamp = (date?: string): number | undefined => {
    if (!date) return undefined;
    const parsed = new Date(date);
    return Number.isNaN(parsed.getTime()) ? undefined : parsed.getTime();
  };

  const handleCreateCoupon = async (values: CouponFormValues) => {
    setSubmitting(true);
    try {
      const payload = {
        companyID: activeCompany ? [activeCompany.id] : [],
        name: values.internalCodeName,
        promoCode: values.promoCode,
        oneUsePerCompany: values.oneUsePerCompany,
        oneUsePerUser: values.oneUsePerUser,
        startDate: Math.floor(
          (parseDateToTimestamp(values.startDate) ?? 0) / 1000,
        ),
        endDate: values.endDate
          ? Math.floor((parseDateToTimestamp(values.endDate) ?? 0) / 1000)
          : null,
        value: Number(values.value),
        unit: values.discountType,
        eventId: currentEvent?.id,
      };

      await createCoupon(payload);
      await refetch();
      setModalOpen(false);
    } catch (error) {
      console.error("Failed to create coupon:", error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateCoupon = async (values: CouponFormValues) => {
    if (!editingCoupon?.id) return;
    setSubmitting(true);

    try {
      const payload = {
        id: editingCoupon.id,
        companyID: activeCompany ? [activeCompany.id] : [],
        name: values.internalCodeName,
        promoCode: values.promoCode,
        oneUsePerCompany: values.oneUsePerCompany,
        oneUsePerUser: values.oneUsePerUser,
        startDate: Math.floor(
          (parseDateToTimestamp(values.startDate) ?? 0) / 1000,
        ),
        endDate: values.endDate
          ? Math.floor((parseDateToTimestamp(values.endDate) ?? 0) / 1000)
          : null,
        value: Number(values.value),
        unit: values.discountType,
        eventId: currentEvent?.id,
        // contentIds: values.contentIds,
      };

      const response = await updateCoupon(payload);
      if (response?.data) {
        await refetch();
        setModalOpen(false);
      }
    } catch (error) {
      console.error("Failed to update coupon:", error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteCoupon = async (row: CouponsRow) => {
    if (!row.id) return;
    setSubmitting(true);

    try {
      await deleteBillingCoupon(row.id);
      await refetch();
    } catch (error) {
      console.error("Failed to delete coupon:", error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleModalSubmit = (values: CouponFormValues) => {
    if (modalMode === "edit") {
      handleUpdateCoupon(values);
    } else {
      handleCreateCoupon(values);
    }
  };

  useEffect(() => {
    refetch();
  }, [currentEvent?.id, activeCompany?.id, refetch]);

  const columns: ColumnDef<CouponsRow>[] = [
    {
      key: "internalCodeName",
      header: "Internal Code Name",
      sortable: true,
      align: "left",
      render: (r) => <span className="font-medium">{r.internalCodeName}</span>,
    },
    {
      key: "promoCode",
      header: "Promo Code",
      sortable: true,
      align: "left",
      render: (r) => <span className="font-medium">{r.promoCode}</span>,
    },
    {
      key: "content",
      header: "Content",
      sortable: false,
      align: "left",
      render: (r) => <span>{r.content}</span>,
    },
    {
      key: "value",
      header: "Value",
      sortable: true,
      align: "left",
      render: (r) => <span className="font-medium">{r.value}</span>,
    },
    {
      key: "discountType",
      header: "Discount Type",
      sortable: true,
      align: "left",
      render: (r) => <span>{r.discountType}</span>,
    },
    {
      key: "startDate",
      header: "Start Date",
      sortable: true,
      align: "left",
      render: (r) => <span>{r.startDate}</span>,
    },
    {
      key: "endDate",
      header: "End Date",
      sortable: true,
      align: "left",
      render: (r) => <span>{r.endDate}</span>,
    },
    {
      key: "singleUseCompany",
      header: "Single Use (Company)",
      sortable: true,
      align: "center",
      render: (r) => <span>{r.singleUseCompany}</span>,
    },
    {
      key: "singleUseAttendee",
      header: "Single Use (Attendee)",
      sortable: true,
      align: "center",
      render: (r) => <span>{r.singleUseAttendee}</span>,
    },
    {
      key: "employeeCode",
      header: "Employee Code",
      sortable: true,
      align: "center",
      render: (r) => <span>{r.employeeCode}</span>,
    },
    {
      key: "usage",
      header: "Usage",
      sortable: true,
      align: "center",
      render: (r) => <span className="font-medium">{r.usage}</span>,
    },
    {
      key: "status",
      header: "Status",
      sortable: true,
      align: "left",
      render: (r) => <span>{humanizeLabel(r.status)}</span>,
    },
    {
      key: "actions",
      header: "Actions",
      pinRight: true,
      sortable: false,
      align: "right",
      render: (r) => (
        <div className="flex items-center justify-end gap-1.5">
          <button
            type="button"
            onClick={() => openEditModal(r)}
            aria-label="Edit coupon"
            title="Edit"
            className="flex h-7 w-7 items-center justify-center rounded-lg text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-neutral-700 dark:hover:bg-neutral-700 dark:hover:text-neutral-200"
          >
            {MODAL_ICONS.edit}
          </button>
          <button
            type="button"
            onClick={() => handleDeleteCoupon(r)}
            aria-label="Delete coupon"
            title="Delete"
            className="flex h-7 w-7 items-center justify-center rounded-lg text-neutral-400 transition-colors hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-950/20 dark:hover:text-red-400"
          >
            {MODAL_ICONS.delete}
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2.5">
        <div className="space-y-2">
          <h1 className="text-2xl font-semibold text-neutral-900 dark:text-neutral-50">
            Coupons
          </h1>
          <TableSearch
            value={search}
            onChange={setSearch}
            placeholder="Search coupons..."
          />
        </div>
        <div className="flex items-center gap-2.5">
          {/* <p className="text-slate-500">
            Revenue: <span className="font-semibold">$0.00</span>
          </p> */}
          <button
            type="button"
            onClick={openCreateModal}
            className="rounded-lg px-4 py-2 text-sm font-medium text-white"
            style={{ backgroundColor: "var(--sidebar-bg)" }}
          >
            Add Coupon
          </button>
        </div>
      </div>

      <Table
        data={filtered}
        columns={columns}
        getRowKey={(r) => r.id}
        enableSort
        enableRowSelection
        stickyHeader
        loading={loading}
        emptyMessage={error ? "No coupons." : "No coupon found."}
      />

      <CouponModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        mode={modalMode}
        initialValues={editingCoupon ? couponToFormValues(editingCoupon) : null}
        contentOptions={contentOptions}
        onSubmit={handleModalSubmit}
        submitting={submitting}
      />
    </div>
  );
}
